import type { Settings } from '../../shared/constants';
import { decodePrefs, encodePrefs, mergePrefs, resolveCreateSettings } from '../../shared/settingsPrefs';

// ============================================================
// v29 BOLT-142 — the web half of sticky settings: localStorage, and nothing
// clever. All the rules live in `shared/settingsPrefs.ts` so iOS reads the same
// ones.
//
// WRITE on every host edit, READ ONCE at create. See the shared module for why
// the read-once shape is the safety property rather than an implementation
// detail (short version: v22 BOLT-103).
// ============================================================

const KEY = 'bolt:settingsPrefs';

function raw(): string | null {
  try {
    return localStorage.getItem(KEY);
  } catch {
    return null; // SSR / private mode ⇒ no prefs, never a crash
  }
}

/** What is stored today (sanitized). */
export function loadPrefs(): Partial<Settings> {
  return decodePrefs(raw());
}

/** Fold one `set_settings` edit into storage. Safe to call on every edit. */
export function rememberSettings(edit: Partial<Settings>): void {
  try {
    localStorage.setItem(KEY, encodePrefs(mergePrefs(decodePrefs(raw()), edit)));
  } catch {
    /* private mode — this session only */
  }
}

/**
 * The settings a NEW game should be created with.
 *
 * `overrides` outranks the stored prefs: it is the deliberate action tier.
 */
export function createSettings(overrides?: Partial<Settings>): Partial<Settings> | undefined {
  return resolveCreateSettings(raw(), overrides);
}

/** Back to global defaults. A wrong sticky setting with no way out is worse
 *  than no stickiness at all (BOLT-142 acceptance #7). */
export function resetPrefs(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* nothing to clear */
  }
}

/** Is anything actually stored? Drives whether the reset control is offered. */
export function hasPrefs(): boolean {
  return Object.keys(loadPrefs()).length > 0;
}
